import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { makeStyles } from '@material-ui/core'
import FlexRow from '../../../../../molecules/FlexRow'
import Loading from '../../../../../molecules/Loading'
import FadeIn from '../../../../../molecules/Transitions/FadeIn'
import CodeBlock from '../blocks/CodeBlock'
import ProposedContentCard from '../blocks/ProposedContentCard'
import MessageChoices from './MessageChoices'

const useStyles = makeStyles(() => ({
  content: {
    '& p': { margin: 0 },
    '& * + p': { marginTop: '0.5em' }
  }
}))

function tryParseChoices (codeString) {
  try {
    const parsed = JSON.parse(codeString)
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].label && parsed[0].value) {
      return { choices: parsed, multiselect: false }
    }
    if (parsed && Array.isArray(parsed.options) && parsed.options.length > 0 && parsed.options[0].label && parsed.options[0].value) {
      return { choices: parsed.options, multiselect: !!parsed.multiselect }
    }
  } catch { /* not valid choices JSON */ }
  return null
}

function MessageBody ({ text, isGenerating, generationStatus, errorMessage }) {
  const classes = useStyles()

  const markdownRenderers = useMemo(() => ({
    pre ({ children }) {
      const codeElement = children?.props ? children : children?.[0]
      const codeClassName = codeElement?.props?.className || ''
      const codeString = String(codeElement?.props?.children || '').replace(/\n$/, '')
      const match = /language-(.*)/.exec(codeClassName)
      const language = (match ? match[1] : '').toLowerCase()

      if (language === 'agent-choices') {
        const result = tryParseChoices(codeString)
        if (result) return <MessageChoices choices={result.choices} multiselect={result.multiselect} />
      }

      if (language === 'jsx') {
        return <ProposedContentCard content={codeString} />
      }

      return <CodeBlock language={language} code={codeString} />
    }
  }), [])

  if (isGenerating) {
    return (
      <FlexRow>
        <span><Loading size={16} /></span>
        <span>{generationStatus || 'Thinking...'}</span>
      </FlexRow>
    )
  }

  if (generationStatus === 'Failed' || errorMessage) {
    return (
      <FadeIn style={{ fontWeight: 'var(--font-weight-thin)' }}>
        <div className='bg-danger-100 text-contrast-danger-100 radius-md py-2 px-2'>
          There was an issue generating a response.  Please try again
        </div>
      </FadeIn>
    )
  }

  return (
    <FadeIn style={{ fontWeight: 'var(--font-weight-thin)' }}>
      <div className={classes.content}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownRenderers}>
          {text}
        </ReactMarkdown>
      </div>
    </FadeIn>
  )
}

MessageBody.propTypes = {
  text: PropTypes.string,
  isGenerating: PropTypes.bool,
  generationStatus: PropTypes.string,
  errorMessage: PropTypes.string
}

export default MessageBody
